/**
 * Configuration service for fetching chat features from the backend
 */

export interface ChatFeatureConfig {
  custom_code_delimiters: boolean;
  fallback_to_markdown: boolean;
  streaming_enabled: boolean;
  file_uploads_enabled: boolean;
  max_upload_mb: number;
  template_library_enabled: boolean;
  chat_share_enabled: boolean;
}

class ConfigService {
  private config: ChatFeatureConfig | null = null;
  private fetchPromise: Promise<ChatFeatureConfig> | null = null;

  /**
   * Get chat feature configuration from backend
   */
  async getConfig(): Promise<ChatFeatureConfig> {
    // Return cached config if available
    if (this.config) {
      return this.config;
    }

    // Return existing promise if fetch is in progress
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Start new fetch
    this.fetchPromise = this.fetchConfig();
    
    try {
      this.config = await this.fetchPromise;
      return this.config;
    } catch (error) {
      // Reset promise on error so we can retry
      this.fetchPromise = null;
      throw error;
    }
  }

  /**
   * Check if custom code delimiters are enabled
   */
  async isCustomDelimitersEnabled(): Promise<boolean> {
    try {
      const config = await this.getConfig();
      return config.custom_code_delimiters;
    } catch (error) {
      console.warn('Failed to get config, defaulting to enabled custom delimiters:', error);
      return true; // Default to enabled for backward compatibility
    }
  }

  /**
   * Check if streaming is enabled
   */
  async isStreamingEnabled(): Promise<boolean> {
    try {
      const config = await this.getConfig();
      return config.streaming_enabled;
    } catch (error) {
      console.warn('Failed to get config, defaulting to enabled streaming:', error);
      return true; // Default to enabled
    }
  }

  /**
   * Get maximum upload size in MB
   */
  async getMaxUploadMB(): Promise<number> {
    try {
      const config = await this.getConfig();
      return config.max_upload_mb;
    } catch (error) {
      console.warn('Failed to get config, defaulting to 20MB upload limit:', error);
      return 20; // Default to 20MB
    }
  }

  /**
   * Check if file uploads are enabled
   */
  async isFileUploadsEnabled(): Promise<boolean> {
    try {
      const config = await this.getConfig();
      return config.file_uploads_enabled;
    } catch (error) {
      console.warn('Failed to get config, defaulting to enabled file uploads:', error);
      return true; // Default to enabled
    }
  }

  /**
   * Check if Template Library is enabled
   */
  async isTemplateLibraryEnabled(): Promise<boolean> {
    try {
      const config = await this.getConfig();
      return config.template_library_enabled;
    } catch (error) {
      console.warn('Failed to get config, defaulting to enabled Template Library:', error);
      return true; // Default to enabled for backward compatibility
    }
  }

  /**
   * Check if chat sharing is enabled
   */
  async isChatShareEnabled(): Promise<boolean> {
    try {
      const config = await this.getConfig();
      return config.chat_share_enabled;
    } catch (error) {
      console.warn('Failed to get config, defaulting to disabled chat sharing:', error);
      return false; // Default to disabled if config fetch fails
    }
  }

  /**
   * Clear cached configuration (useful for testing or when backend config changes)
   */
  clearCache(): void {
    this.config = null;
    this.fetchPromise = null;
  }

  /**
   * Private method to fetch configuration from backend
   */
  private async fetchConfig(): Promise<ChatFeatureConfig> {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
    
    const response = await fetch(`${apiUrl}/api/config/features`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication if needed
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch config: ${response.status} ${response.statusText}`);
    }

    const config: ChatFeatureConfig = await response.json();
    
    // Validate the config structure
    this.validateConfig(config);
    
    return config;
  }

  /**
   * Validate configuration structure
   */
  private validateConfig(config: any): asserts config is ChatFeatureConfig {
    const required = [
      'custom_code_delimiters',
      'fallback_to_markdown', 
      'streaming_enabled',
      'file_uploads_enabled',
      'max_upload_mb',
      'template_library_enabled',
      'chat_share_enabled',
    ];

    for (const field of required) {
      if (!(field in config)) {
        throw new Error(`Missing required config field: ${field}`);
      }
    }

    // Type validation
    if (typeof config.custom_code_delimiters !== 'boolean') {
      throw new Error('custom_code_delimiters must be boolean');
    }
    if (typeof config.fallback_to_markdown !== 'boolean') {
      throw new Error('fallback_to_markdown must be boolean');
    }
    if (typeof config.streaming_enabled !== 'boolean') {
      throw new Error('streaming_enabled must be boolean');
    }
    if (typeof config.file_uploads_enabled !== 'boolean') {
      throw new Error('file_uploads_enabled must be boolean');
    }
    if (typeof config.max_upload_mb !== 'number' || config.max_upload_mb <= 0) {
      throw new Error('max_upload_mb must be a positive number');
    }
    if (typeof config.template_library_enabled !== 'boolean') {
      throw new Error('template_library_enabled must be boolean');
    }
  }
}

// Export singleton instance
export const configService = new ConfigService();

// Export hook for React components
export function useConfigService() {
  return configService;
}